<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style>
/* ===== Design System: Poker Stats - Glassmorphism ===== */

:root {
  /* Color Palette */
  --color-primary: #7C3AED;
  --color-primary-light: #A78BFA;
  --color-primary-dark: #5B21B6;
  --color-secondary: #A78BFA;
  --color-cta: #F43F5E;
  --color-cta-light: #FB7185;
  --color-bg: #0F0F23;
  --color-bg-light: #1a1a3e;
  --color-bg-card: #161637;
  --color-text: #E2E8F0;
  --color-text-muted: #94A3B8;
  --color-text-dim: #64748B;
  --color-success: #10B981;
  --color-danger: #EF4444;
  --color-warning: #F59E0B;
  --color-info: #3B82F6;

  /* Glass Effect */
  --glass-bg: rgba(255, 255, 255, 0.06);
  --glass-bg-hover: rgba(255, 255, 255, 0.1);
  --glass-border: rgba(255, 255, 255, 0.12);
  --glass-border-hover: rgba(255, 255, 255, 0.2);
  --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  --glass-blur: blur(16px) saturate(180%);

  /* Spacing */
  --space-xs: 0.25rem;
  --space-sm: 0.5rem;
  --space-md: 1rem;
  --space-lg: 1.5rem;
  --space-xl: 2rem;
  --space-2xl: 3rem;
  --space-3xl: 4rem;

  /* Border Radius */
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 20px;
  --radius-full: 9999px;

  /* Shadows */
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.2);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.25);
  --shadow-lg: 0 10px 24px rgba(0, 0, 0, 0.3);
  --shadow-xl: 0 20px 40px rgba(0, 0, 0, 0.35);
  --shadow-glow-primary: 0 0 20px rgba(124, 58, 237, 0.3);
  --shadow-glow-cta: 0 0 20px rgba(244, 63, 94, 0.3);

  /* Transitions */
  --transition-fast: 150ms ease;
  --transition-normal: 250ms ease;
  --transition-slow: 400ms ease;
}

/* ===== Base Styles ===== */
* {
  box-sizing: border-box;
}

body {
  font-family: 'Fira Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  margin: 0;
  padding: 0;
  background: var(--color-bg);
  background-image:
    radial-gradient(ellipse at 20% 50%, rgba(124, 58, 237, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(244, 63, 94, 0.06) 0%, transparent 50%),
    radial-gradient(ellipse at 50% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%);
  background-attachment: fixed;
  color: var(--color-text);
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ===== Typography ===== */
h1, h2, h3, h4, h5, h6 {
  font-family: 'Fira Code', monospace;
  color: var(--color-text);
  font-weight: 600;
}

.font-mono {
  font-family: 'Fira Code', monospace;
}

/* ===== Glass Card System ===== */
.glass-card {
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--glass-shadow);
  transition: all var(--transition-normal);
}

.glass-card:hover {
  border-color: var(--glass-border-hover);
  box-shadow: var(--shadow-lg);
}

.glass-card-interactive {
  cursor: pointer;
}

.glass-card-interactive:hover {
  transform: translateY(-2px);
  background: var(--glass-bg-hover);
}

/* ===== Override Bootstrap Cards ===== */
.card {
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg) !important;
  box-shadow: var(--glass-shadow);
  color: var(--color-text);
  transition: all var(--transition-normal);
}

.card:hover {
  border-color: var(--glass-border-hover);
}

.card-header {
  background: rgba(255, 255, 255, 0.04) !important;
  border-bottom: 1px solid var(--glass-border) !important;
  color: var(--color-text);
  padding: var(--space-md) var(--space-lg);
}

.card-body {
  color: var(--color-text);
  padding: var(--space-lg);
}

.card-footer {
  background: rgba(255, 255, 255, 0.04) !important;
  border-top: 1px solid var(--glass-border) !important;
  color: var(--color-text);
}

.card-title {
  color: var(--color-text);
  font-family: 'Fira Code', monospace;
}

/* ===== Override Bootstrap Tables ===== */
.table {
  color: var(--color-text);
  --bs-table-bg: transparent;
  --bs-table-hover-bg: rgba(255, 255, 255, 0.05);
  --bs-table-striped-bg: rgba(255, 255, 255, 0.03);
  border-color: var(--glass-border);
}

.table > :not(caption) > * > * {
  border-bottom-color: var(--glass-border);
  background-color: transparent;
  color: var(--color-text);
}

.table thead th {
  font-family: 'Fira Code', monospace;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  border-bottom: 2px solid rgba(124, 58, 237, 0.3) !important;
  padding: var(--space-md);
}

.table-hover > tbody > tr:hover > * {
  background-color: rgba(124, 58, 237, 0.08) !important;
  color: var(--color-text);
}

.table-secondary {
  --bs-table-bg: rgba(124, 58, 237, 0.1) !important;
  --bs-table-border-color: var(--glass-border) !important;
}

.table-secondary > * {
  color: var(--color-text) !important;
  background-color: rgba(124, 58, 237, 0.1) !important;
}

.table-light {
  --bs-table-bg: rgba(255, 255, 255, 0.05) !important;
}

.table-light > * {
  color: var(--color-text) !important;
}

.table-borderless > :not(caption) > * > * {
  border-bottom-color: transparent;
}

/* ===== Override Bootstrap Buttons ===== */
.btn {
  font-family: 'Fira Sans', sans-serif;
  font-weight: 500;
  border-radius: var(--radius-sm);
  transition: all var(--transition-normal);
  cursor: pointer;
}

.btn-primary {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.btn-primary:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  transform: translateY(-1px);
  box-shadow: var(--shadow-glow-primary);
}

.btn-danger {
  background: var(--color-cta);
  border-color: var(--color-cta);
}

.btn-danger:hover {
  background: #E11D48;
  border-color: #E11D48;
  transform: translateY(-1px);
  box-shadow: var(--shadow-glow-cta);
}

.btn-success {
  background: var(--color-success);
  border-color: var(--color-success);
}

.btn-success:hover {
  background: #059669;
  border-color: #059669;
  transform: translateY(-1px);
}

.btn-warning {
  background: var(--color-warning);
  border-color: var(--color-warning);
  color: #0F0F23;
}

.btn-warning:hover {
  background: #D97706;
  border-color: #D97706;
  transform: translateY(-1px);
}

.btn-outline-primary {
  color: var(--color-primary-light);
  border-color: var(--color-primary-light);
}

.btn-outline-primary:hover {
  background: var(--color-primary);
  border-color: var(--color-primary);
  transform: translateY(-1px);
}

.btn-outline-secondary {
  color: var(--color-text-muted);
  border-color: var(--glass-border);
}

.btn-outline-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--glass-border-hover);
  color: var(--color-text);
}

.btn-outline-danger {
  color: var(--color-cta);
  border-color: var(--color-cta);
}

.btn-outline-danger:hover {
  background: var(--color-cta);
  border-color: var(--color-cta);
  color: #fff;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--glass-border);
  color: var(--color-text);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: var(--glass-border-hover);
  color: var(--color-text);
}

.btn-link {
  color: var(--color-primary-light);
}

.btn-link:hover {
  color: var(--color-primary);
}

/* Gradient CTA Button */
.btn-gradient {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-cta) 100%);
  border: none;
  color: #fff;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-sm);
  transition: all var(--transition-normal);
  cursor: pointer;
}

.btn-gradient:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(124, 58, 237, 0.4);
  color: #fff;
}

/* ===== Override Bootstrap Forms ===== */
.form-control,
.form-select {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  padding: 0.625rem 0.875rem;
  transition: all var(--transition-normal);
  font-size: 16px;
}

.form-control:focus,
.form-select:focus {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--color-primary);
  color: var(--color-text);
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.2);
}

.form-control::placeholder {
  color: var(--color-text-dim);
}

.form-control:disabled,
.form-control[readonly] {
  background: rgba(255, 255, 255, 0.03);
  color: var(--color-text-muted);
}

.form-label {
  color: var(--color-text-muted);
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.375rem;
}

.form-text {
  color: var(--color-text-dim);
}

.input-group .btn {
  border-color: var(--glass-border);
}

.form-control.is-invalid {
  border-color: var(--color-cta);
}

.invalid-feedback {
  color: var(--color-cta-light);
}

/* ===== Override Bootstrap Modals ===== */
.modal-content {
  background: var(--color-bg-card);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg) !important;
  box-shadow: var(--shadow-xl);
  color: var(--color-text);
}

.modal-header {
  border-bottom: 1px solid var(--glass-border);
  padding: var(--space-lg);
}

.modal-header .modal-title {
  font-family: 'Fira Code', monospace;
  color: var(--color-text);
}

.modal-header .btn-close {
  filter: invert(1);
  opacity: 0.6;
}

.modal-header .btn-close:hover {
  opacity: 1;
}

.modal-body {
  color: var(--color-text);
  padding: var(--space-lg);
}

.modal-footer {
  border-top: 1px solid var(--glass-border);
  padding: var(--space-md) var(--space-lg);
}

.modal-header.bg-warning {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%) !important;
  border-bottom: 1px solid rgba(245, 158, 11, 0.3);
}

.modal-header.bg-warning .modal-title {
  color: var(--color-warning);
}

.modal-header.bg-primary {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.3) 0%, rgba(124, 58, 237, 0.1) 100%) !important;
  border-bottom: 1px solid rgba(124, 58, 237, 0.3);
}

.modal-header.bg-primary .modal-title {
  color: var(--color-primary-light);
}

.modal-header.bg-gradient-primary {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.3) 0%, rgba(244, 63, 94, 0.15) 100%) !important;
  border-bottom: 1px solid rgba(124, 58, 237, 0.3);
}

.modal-backdrop {
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

/* ===== Override Bootstrap Badges ===== */
.badge {
  font-family: 'Fira Code', monospace;
  font-weight: 500;
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
  border-radius: var(--radius-full);
}

.badge.bg-primary {
  background: rgba(124, 58, 237, 0.25) !important;
  color: var(--color-primary-light);
  border: 1px solid rgba(124, 58, 237, 0.3);
}

.badge.bg-success {
  background: rgba(16, 185, 129, 0.2) !important;
  color: #34D399;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.badge.bg-danger {
  background: rgba(239, 68, 68, 0.2) !important;
  color: #F87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.badge.bg-warning {
  background: rgba(245, 158, 11, 0.2) !important;
  color: #FBBF24;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.badge.bg-info {
  background: rgba(59, 130, 246, 0.2) !important;
  color: #60A5FA;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

/* ===== Override Bootstrap Alerts ===== */
.alert {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
}

.alert-danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #F87171;
}

.alert-warning {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.3);
  color: #FBBF24;
}

.alert-info {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #60A5FA;
}

.alert-success {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
  color: #34D399;
}

/* ===== Override Bootstrap List Groups ===== */
.list-group-item {
  background: transparent;
  border-color: var(--glass-border);
  color: var(--color-text);
  transition: background var(--transition-fast);
}

.list-group-item:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* ===== Override Bootstrap Dropdowns ===== */
.dropdown-menu {
  background: var(--color-bg-card);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.dropdown-item {
  color: var(--color-text);
  transition: all var(--transition-fast);
}

.dropdown-item:hover {
  background: rgba(124, 58, 237, 0.15);
  color: var(--color-primary-light);
}

/* ===== Override Bootstrap Spinner ===== */
.spinner-border {
  color: var(--color-primary-light);
}

/* ===== Text Utilities ===== */
.text-muted {
  color: var(--color-text-muted) !important;
}

.text-success {
  color: var(--color-success) !important;
}

.text-danger {
  color: var(--color-danger) !important;
}

.text-primary {
  color: var(--color-primary-light) !important;
}

.text-warning {
  color: var(--color-warning) !important;
}

.text-info {
  color: var(--color-info) !important;
}

.text-dark {
  color: var(--color-text) !important;
}

/* ===== Background Utilities ===== */
.bg-light {
  background: rgba(255, 255, 255, 0.04) !important;
}

.bg-white {
  background: var(--color-bg-card) !important;
}

/* ===== Link Styles ===== */
a {
  color: var(--color-primary-light);
  text-decoration: none;
  transition: color var(--transition-fast);
}

a:hover {
  color: var(--color-primary);
}

/* ===== Scrollbar ===== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.02);
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* ===== Selection ===== */
::selection {
  background: rgba(124, 58, 237, 0.3);
  color: #fff;
}

/* ===== Number / Money Display ===== */
.money-value,
.stat-number {
  font-family: 'Fira Code', monospace;
  font-weight: 600;
}

/* ===== Reduced Motion ===== */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* ===== Border-top override ===== */
.border-top {
  border-top-color: var(--glass-border) !important;
}

.border-bottom {
  border-bottom-color: var(--glass-border) !important;
}

/* ===== Page transition ===== */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.2s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

/* Override container-fluid padding for dark theme */
.container-fluid {
  color: var(--color-text);
}

/* ===== Input Group ===== */
.input-group-text {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--glass-border);
  color: var(--color-text-muted);
}

/* ===== Nav Tabs global ===== */
.nav-tabs {
  border-bottom-color: var(--glass-border);
}

/* ===== Table bordered ===== */
.table-bordered > :not(caption) > * > * {
  border-color: var(--glass-border);
}

/* ===== Form Select Option for dark bg ===== */
.form-select option,
.form-control option {
  background: var(--color-bg-card);
  color: var(--color-text);
}

/* ===== Pagination global ===== */
.page-link {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--glass-border);
  color: var(--color-text-muted);
}

.page-link:hover {
  background: rgba(124, 58, 237, 0.15);
  border-color: var(--color-primary);
  color: var(--color-primary-light);
}

.page-item.active .page-link {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.page-item.disabled .page-link {
  background: rgba(255, 255, 255, 0.03);
  color: var(--color-text-dim);
}
</style>
